/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        types/index.ts
 * @description Definitions TypeScript pour toutes les entites du module
 *              Ces types correspondent aux entites Doctrine du backend Symfony
 *
 * @contents    - Enumerations (StatutDeneignement, ZoneDeneignement, etc.)
 *              - Interfaces d'entites (User, Compagnie, Parking, Deneignement...)
 *              - DTOs pour les formulaires et API
 *              - Types de filtres et pagination
 *              - Constantes de labels et couleurs pour l'affichage
 *
 * @sync        Ces types doivent rester synchronises avec:
 *              - backend/src/Entity/*.php
 *              - backend/src/Enum/*.php
 *
 * @version     2.1
 * @author      Equipe FALCON
 * =============================================================================
 */

// =============================================================================
// ENUMERATIONS
// Valeurs correspondant aux Enums PHP du backend
// =============================================================================
// SFD v2.1: Nouveaux statuts d'intervention
export type StatutDeneignement = 'en_attente' | 'affectee' | 'en_deplacement' | 'en_cours' | 'cloturee' | 'validee' | 'annulee';
export type ZoneDeneignement = 'complete' | 'avion' | 'voies' | 'equipements';
export type TypeIntervention = 'leger' | 'standard' | 'lourd' | 'preventif';
export type PrioriteDeneignement = 'normale' | 'urgente' | 'critique';
export type StatutParking = 'disponible' | 'en_deneignement' | 'indisponible';
// SFD jalonnement Contact/Large (Oct 2026)
export type SituationParking = 'contact' | 'large';
export type ConfigurationJalon = 'contact_occupe' | 'large_occupe' | 'contact_libre' | 'large_libre';

// Entites principales
export interface User {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  telephone?: string;
  roles: string[];
  actif: boolean;
  isTotpEnabled?: boolean;
  nomComplet: string;
  // Compagnies aeriennes visibles par cet utilisateur - n'a de sens que
  // pour ROLE_COMPAGNIE (restriction appliquee cote backend).
  compagnies?: Compagnie[];
  // Autorise un utilisateur ROLE_COMPAGNIE a creer lui-meme une demande de
  // deneigement - desactive par defaut (optionnable, cf backend).
  autoriseCreationIntervention?: boolean;
  // Qualifications detenues (conduite Pony/Tracteur) - n'a de sens que pour
  // ROLE_AGENT_DENEIGNEMENT (SFD "planification des vehicules et equipages").
  qualifications?: AgentQualification[];
}

export interface Qualification {
  id: number;
  code: string;
  libelle: string;
  // Code d'un type actif du referentiel TypeMateriel (voir TypesMaterielPage) -
  // simple chaine, pas une liste figee (cf la meme correction faite sur
  // CompatibiliteEngin).
  type?: string | null;
  actif?: boolean;
}

export interface AgentQualification {
  id: number;
  qualification: Qualification;
  valideJusquau?: string | null;
  expiree: boolean;
}

export interface Compagnie {
  id: number;
  codeIata: string;
  nom: string;
  logo?: string;
  actif: boolean;
}

export interface Parking {
  id: number;
  code: string;
  nom?: string;
  aeroport: string;
  statutHiver: StatutParking;
  actif: boolean;
  situation: SituationParking | null;
  /** Position geographique (WGS84), pour affichage sur une carte - null si non geolocalise. */
  latitude: number | null;
  longitude: number | null;
}

export interface EquipeDeneignement {
  id: number;
  nom: string;
  code: string;
  actif: boolean;
  chef?: User;
  /** Vehicule (referentiel materiel) affecte a l'equipe. */
  engin?: MaterielDeneignement | null;
  agents: User[];
  nombreAgents: number;
  /**
   * Vacation du jour du chef assigne (`chef`), ou null si l'equipe n'a pas
   * de chef ou que son chef n'est pas au planning aujourd'hui - n'afficher
   * le chef sur l'ecran Equipes que lorsque ce champ est non-null.
   */
  chefVacation?: { date: string; heureDebut: string; heureFin: string } | null;
}

export interface MaterielDeneignement {
  id: number;
  nom: string;
  type: string;
  immatriculation?: string;
  // Etat operationnel (remplace l'ancien booleen "disponible", fusion avec
  // l'ancien referentiel Engin - SFD "planification des vehicules et equipages").
  etat: 'disponible' | 'panne' | 'maintenance';
  actif: boolean;
  // Calcule par le backend (etat === 'disponible') - conserve pour les
  // affichages existants.
  disponible: boolean;
}

export interface DeneignementHistorique {
  id: number;
  ancienStatut?: string;
  nouveauStatut: string;
  user: User;
  commentaire?: string;
  horodatage: string;
  action?: string;
  description: string;
}

export interface Deneignement {
  id: number;
  numeroOrdre: string;
  dateIntervention: string;
  heureSouhaitee: string;
  compagnie: Compagnie;
  zone: ZoneDeneignement;
  typeIntervention: TypeIntervention;
  priorite: PrioriteDeneignement;
  epaisseurNeige?: number;
  immatriculationAvion?: string | null;
  commentaire?: string;
  statut: StatutDeneignement;
  heureDebutReelle?: string;
  heureFinReelle?: string;
  dureeIntervention?: number;
  photoAvant?: string;
  photoApres?: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: User;
  validatedBy?: User;
  validatedAt?: string;
  parking?: Parking;  // SFD v2.1: 1 demande = 1 parking
  equipes: EquipeDeneignement[];
  materiels: MaterielDeneignement[];
  historiques: DeneignementHistorique[];
  // SFD v2.2: Champs pour cloture sans travaux
  travauxEffectues?: boolean;
  motifNonRealisation?: string;
  commentaireNonRealisation?: string;
  // SFD jalonnement Contact/Large (Oct 2026)
  posteOccupe?: boolean | null;
  configuration?: ConfigurationJalon | null;
  nombreJalonsAttendus?: number | null;
  /** RG-05 : false des qu'au moins un jalon a demarre */
  etatPosteModifiable?: boolean;
}

// DTOs pour les formulaires
export interface CreateDeneignementDTO {
  dateIntervention: string;
  heureSouhaitee: string;
  compagnieId: number;
  parkingId: number;  // SFD v2.1: 1 demande = 1 parking
  zone: ZoneDeneignement;
  typeIntervention: TypeIntervention;
  priorite: PrioriteDeneignement;
  epaisseurNeige?: number;
  immatriculationAvion?: string | null;
  commentaire?: string;
  /**
   * SFD jalonnement Contact/Large : etat du poste a la creation, obligatoire.
   * Determine avec la situation du parking la configuration de jalonnement
   * (RG-02) et le nombre de jalons crees (5, 7 ou 1).
   */
  posteOccupe?: boolean;
  /**
   * Confirme la creation malgre un chevauchement non bloquant (meme parking, meme
   * jour, horaire different). Omis ou false lors du premier envoi ; renvoye a true
   * si l'utilisateur confirme apres l'avertissement du backend (409
   * requiresConfirmation).
   */
  confirmerCreation?: boolean;
}

/** Conflit non bloquant retourne par l'API lors d'une creation (meme parking/jour, horaire different) */
export interface ConflitMemeJour {
  numeroOrdre: string;
  heureSouhaitee: string;
  statut: StatutDeneignement;
}

export interface UpdateDeneignementDTO extends Partial<CreateDeneignementDTO> {}

export interface AffecterEquipeDTO {
  equipeId: number;
}

export interface AnnulerDTO {
  motif: string;
}

// Types pour les filtres
export interface DeneignementFilters {
  statut?: StatutDeneignement;
  dateDebut?: string;
  dateFin?: string;
  parking?: number;
  equipe?: number;
  compagnie?: number;
  priorite?: PrioriteDeneignement;
  type?: TypeIntervention;
  /**
   * 'recent' : tri par date de creation descendante, sans priorite. Utilise par le
   * widget "Interventions recentes" du tableau de bord pour que les interventions
   * tout juste creees soient toujours visibles, meme en priorite Normale.
   */
  sortBy?: 'recent';
  /** Tri explicite sur une colonne (page Interventions, clic sur l'en-tete) */
  sortField?: 'numeroOrdre' | 'dateIntervention' | 'compagnie' | 'parking' | 'typeIntervention' | 'priorite' | 'statut';
  sortDirection?: 'asc' | 'desc';
}

// Types pour la pagination
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// Types pour l'authentification
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: User;
}

// Constantes pour les labels
// SFD v2.1: Nouveaux statuts
export const STATUT_LABELS: Record<StatutDeneignement, string> = {
  en_attente: 'En attente',
  affectee: 'Affectee',
  en_deplacement: 'En deplacement',
  en_cours: 'En cours',
  cloturee: 'Cloturee',
  validee: 'Validee',
  annulee: 'Annulee',
};

export const STATUT_COLORS: Record<StatutDeneignement, string> = {
  en_attente: 'bg-blue-500',
  affectee: 'bg-orange-500',
  en_deplacement: 'bg-violet-500',
  en_cours: 'bg-red-500',
  cloturee: 'bg-lime-500',
  validee: 'bg-green-500',
  annulee: 'bg-gray-500',
};

export const ZONE_LABELS: Record<ZoneDeneignement, string> = {
  complete: 'Complete',
  avion: 'Zone Avion',
  voies: 'Voies de circulation',
  equipements: 'Equipements',
};

export const TYPE_LABELS: Record<TypeIntervention, string> = {
  leger: 'Leger',
  standard: 'Standard',
  lourd: 'Lourd',
  preventif: 'Preventif',
};

export const PRIORITE_LABELS: Record<PrioriteDeneignement, string> = {
  normale: 'Normale',
  urgente: 'Urgente',
  critique: 'Critique',
};

// =====================================================
// SFD v2.1: Types pour les zones de deneignement
// =====================================================

export type StatutZone = 'a_faire' | 'en_cours' | 'termine';
export type TypeEngin = 'pony' | 'tracteur';

export interface Zone {
  id: number;
  numero: number;
  nom: string;
  description?: string;
  // Null pour les jalons (SFD Contact/Large) : concept propre aux 6 zones
  // historiques (repartition pony/tracteur).
  typeEnginRequis: TypeEngin | null;
  typeEnginRequisLabel: string | null;
  ordre: number;
  actif: boolean;
  // Null pour les 6 zones historiques (seuls les jalons ont un libelle dedie).
  libelle: string | null;
  // Renseignes uniquement pour les jalons (SFD Contact/Large).
  configuration?: ConfigurationJalon | null;
  pinX?: number | null;
  pinY?: number | null;
}

export type MomentPhoto = 'avant' | 'apres';

export interface Photo {
  id: number;
  moment: MomentPhoto;
  momentLabel: string;
  url: string;
  createdAt: string;
}

export interface EnginPlanifieResume {
  id: number;
  engin?: {
    id: number;
    nom: string;
    type: string;
    immatriculation: string | null;
  };
}

export interface DeneignementZone {
  id: number;
  zone: Zone;
  equipe?: EquipeDeneignement;
  // Vehicule auquel ce jalon est effectivement affecte (Lot VH-4, #VH55,
  // RG-VH-13) - null pour une intervention anterieure a ce lot ou un jalon
  // pas encore affecte (CA-VH-14, `equipe` ci-dessus reste la source pour
  // l'historique).
  enginPlanifie?: EnginPlanifieResume | null;
  statut: StatutZone;
  statutLabel: string;
  statutColor: string;
  heureDebut?: string;
  heureFin?: string;
  commentaire?: string;
  dureeMinutes?: number;
  // Photos par jalon (SFD jalonnement Contact/Large, Lot 4 - #121),
  // toujours presentes (eventuellement vides) pour les jalons du nouveau
  // referentiel ; absentes pour les 6 zones historiques pre-Lot-4.
  photos?: Photo[];
}

export interface DeneignementZonesResponse {
  zones: DeneignementZone[];
  progression: number;
  stats: {
    a_faire: number;
    en_cours: number;
    termine: number;
  };
}

export interface UpdateZoneStatutDTO {
  statut: StatutZone;
  commentaire?: string;
}

export interface AssignEquipeDTO {
  equipe_id: number | null;
}

// Labels et couleurs pour les statuts de zone
export const STATUT_ZONE_LABELS: Record<StatutZone, string> = {
  a_faire: 'A faire',
  en_cours: 'En cours',
  termine: 'Termine',
};

export const STATUT_ZONE_COLORS: Record<StatutZone, string> = {
  a_faire: 'bg-gray-400',
  en_cours: 'bg-blue-500',
  termine: 'bg-green-500',
};

export const TYPE_ENGIN_LABELS: Record<TypeEngin, string> = {
  pony: 'Pony',
  tracteur: 'Tracteur',
};

export const TYPE_ENGIN_COLORS: Record<TypeEngin, string> = {
  pony: 'bg-amber-500',
  tracteur: 'bg-indigo-500',
};
